import { isAxiosError } from 'axios'
import type { RouteLocation, RouteLocationRaw, RouteRecordRaw } from 'vue-router'

import { UUID } from '@/config'
import { errorParser } from '@/helpers'
import { useCorporaStore, useNotificationStore, useProcessStore } from '@/stores'
import type { UUID as UUIDType } from '@/types'
import Awaiter, { type AwaiterProps } from '@/views/Awaiter.vue'

async function corpusRetrieve(corpusId: UUIDType): Promise<RouteLocationRaw | null> {
  const corporaStore = useCorporaStore()

  // Force the store to reload the corpora list
  corporaStore.loaded = false
  await corporaStore.list()

  // When the corpus is loaded, redirect to the elements navigation within this corpus
  if (corporaStore.corpora[corpusId])
    return {
      name: 'navigation',
      params: { corpusId },
    }
  else return null
}

async function processRetrieve(id: UUIDType): Promise<RouteLocationRaw | null> {
  const notificationStore = useNotificationStore()
  const processStore = useProcessStore()
  try {
    const process = await processStore.retrieve(id)

    /*
     * For an unstarted Workers process, redirect to the elements filter page;
     * For an unstarted Dataset process, redirect to the dataset sets selection page;
     * For started processes or processes of other modes, redirect to the process details page.
     * This allows this waiting page to be used for all new templates, imports, exports, etc.
     */
    if (process.tasks.length > 0 || !['dataset', 'workers'].includes(process.mode))
      return {
        name: 'process-details',
        params: {
          id,
          selectedRun: 0,
        },
      }
    else if (process.mode === 'workers')
      return {
        name: 'process-filter',
        params: { id },
      }
    else if (process.mode === 'dataset')
      return {
        name: 'process-datasets',
        params: { id },
      }
    else if (process.mode === 'template')
      return {
        name: 'process-configure',
        params: { id },
      }
    throw new Error('Unexpected process reached')
  } catch (err) {
    if (isAxiosError(err)) {
      notificationStore.notify({
        type: 'error',
        text: errorParser(err),
      })
      return null
    }
    throw err
  }
}

const awaiterRoutes: RouteRecordRaw[] = [
  {
    path: `/corpus/:corpusId(${UUID})/wait`,
    name: 'corpus-wait',
    component: Awaiter,
    meta: {
      requiresLogin: true,
      requiresVerified: true,
      requiresFeatures: ['enterprise'],
    },
    props: (route: RouteLocation): AwaiterProps => ({
      resourceType: 'project',
      id: route.params.corpusId.toString(),
      retrieveFunction: corpusRetrieve,
    }),
  },
  {
    path: `/process/:id(${UUID})/wait`,
    name: 'process-wait',
    component: Awaiter,
    meta: {
      requiresLogin: true,
      requiresFeatures: ['enterprise'],
    },
    props: (route: RouteLocation): AwaiterProps => ({
      resourceType: 'process',
      id: route.params.id.toString(),
      retrieveFunction: processRetrieve,
    }),
  },
]

export default awaiterRoutes
